import React, { useEffect, useState, useRef } from 'react';
import { FaTrash, FaPlus, FaSearch, FaCalendarAlt, FaTimes, FaArrowUp, FaArrowDown, FaUpload, FaEye, FaMapMarkerAlt, FaCalendarCheck, FaInfoCircle } from 'react-icons/fa';
import { FaPencil } from "react-icons/fa6";
import { motion, AnimatePresence } from 'framer-motion';
import ApiService from '../../models/ApiService';

const getDefaultImage = (name) => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Festival')}&background=random&size=128`;
};

// Helper function to normalize API data to component format
const normalizeFestivalData = (apiFestival) => {
  return {
    id: apiFestival.id || apiFestival._id,
    name: apiFestival.festival_name || apiFestival.name || '',
    urdu_name: apiFestival.festival_urdu || apiFestival.urdu || apiFestival.urdu_name || '',
    hindi_name: apiFestival.festival_hindi || apiFestival.hindi || apiFestival.hindi_name || '',
    description: apiFestival.description || '',
    brief_description: apiFestival.brief_description || '',
    year: apiFestival.year || '',
    place: apiFestival.place || '',
    image_url: apiFestival.image_url || apiFestival.image || null,
    is_published: apiFestival.is_published !== undefined ? apiFestival.is_published : true,
    no: apiFestival.no || apiFestival.id || 0
  };
};

// Festival Card Component
const FestivalCard = ({ festival, onEdit, onDelete, onToggleStatus, onView }) => {
  return (
    <div
      className={`bg-white rounded-lg shadow-md overflow-hidden border border-[--border-color-g] hover:shadow-lg transition-all duration-300 
        ${festival.is_published === false ? 'opacity-60' : ''}`}
    >
      {/* Festival Image */}
      <div className="relative h-40 overflow-hidden group cursor-pointer" onClick={() => onView(festival)}>
        {festival.image_url ? (
          <img
            src={festival.image_url}
            alt={festival.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '';
              e.target.parentElement.innerHTML = `
                <div class="w-full h-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                  <svg class="text-white text-4xl" ...></svg>
                </div>`;
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <FaCalendarAlt className="text-white text-5xl" />
          </div>
        )}
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
          <FaEye className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      </div>

      <div className="p-4">
        {/* Festival Name */}
        <div className="text-center mb-3">
          <h3 className="text-xl font-bold text-[--text-color-g] cursor-pointer hover:text-[--primary-color] transition-colors" onClick={() => onView(festival)}>
            {festival.name}
          </h3>
          {festival.urdu_name && (
            <p className="text-[--text-color-g] mt-1 mb-4" dir="rtl" id="primary-urdu-fonts">
              {festival.urdu_name}
            </p>
          )}
          {festival.hindi_name && (
            <p className="text-[--text-color-g]">
              {festival.hindi_name}
            </p>
          )}
        </div>

        {/* Brief Description Preview */}
        {festival.brief_description && (
          <div className="mb-2 text-center">
            <p className="text-xs text-[--text-color-g] italic line-clamp-2">
              {festival.brief_description}
            </p>
          </div>
        )}

        {/* Year & Place Badges */}
        {/* {(festival.year || festival.place) && (
          <div className="flex justify-center gap-2 mb-3">
            {festival.year && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center gap-1">
                <FaCalendarCheck size={10} /> {festival.year}
              </span>
            )}
            {festival.place && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1">
                <FaMapMarkerAlt size={10} /> {festival.place}
              </span>
            )}
          </div>
        )} */}

        <div className="flex justify-between items-center pt-3 border-t border-[--border-color-g]">

          {/* View Button */}
          <button
            onClick={() => onView(festival)}
            className="p-1.5  text-[--primary-btn-color] hover:text-[--secondary-btn-color] hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <FaInfoCircle size={14} />
          </button>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded-lg transition-colors">
              <input
                type="checkbox"
                checked={festival.is_published !== false}
                onChange={() => onToggleStatus(festival.id, festival.is_published)}
                className="hidden"
              />
              <div className={`w-7 h-3.5 flex items-center rounded-full p-0.5 transition-colors 
                ${festival.is_published !== false ? 'bg-green-500' : 'bg-gray-400'}`}>
                <div className={`bg-white w-2.5 h-2.5 rounded-full shadow-md transform transition-transform duration-200 
                  ${festival.is_published !== false ? 'translate-x-3.5' : 'translate-x-0'}`} />
              </div>
            </label>
            <button
              onClick={() => onEdit(festival)}
              className="p-1.5 text-[--primary-btn-color] hover:bg-[--primary-btn-color] hover:text-white rounded-full transition-colors"
              title="Edit"
            >
              <FaPencil size={14} />
            </button>
            <button
              onClick={() => onDelete(festival.id)}
              className="p-1.5 text-red-500 hover:bg-red-500 hover:text-white rounded-full transition-colors"
              title="Delete"
            >
              <FaTrash size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Detail View Modal Component
const DetailViewModal = ({ isOpen, onClose, festival }) => {
  if (!isOpen || !festival) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-[--primary-color]">
                {festival.name}
              </h2>
              <button
                onClick={onClose}
                className="text-black hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Image and Main Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Image Column */}
              <div className="md:col-span-1">
                <div className="w-32 h-32 mx-auto rounded-full from-[--primary-btn-color] to-[--secondary-btn-color] p-2">
                  <img
                    src={festival.image_url || getDefaultImage(festival.name)}
                    alt={festival.name}
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getDefaultImage(festival.name);
                    }}
                  />
                </div>
              </div>

              {/* Info Column */}
              <div className="md:col-span-2">
                {/* Urdu Name */}
                {festival.urdu_name && (
                  <div className="text-center md:text-left">
                    <div className="text-lg p-3 text-[--text-color-g]" id='primary-urdu-fonts' dir="rtl">
                      {festival.urdu_name}
                    </div>
                  </div>
                )}

                {/* Hindi Name */}
                {festival.hindi_name && (
                  <div className="text-center md:text-left">
                    <div className="text-md text-[--text-color-g] mt-1">
                      {festival.hindi_name}
                    </div>
                  </div>
                )}

                {/* Meta Information */}
                <div className="mt-4 space-y-2">
                  {festival.year && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FaCalendarCheck className="mr-2 text-[--primary-btn-color]" />
                      <span className="font-medium text-black">Year:</span>
                      <span className="ml-2">{festival.year}</span>
                    </div>
                  )}

                  {festival.place && (
                    <div className="flex items-center text-sm text-gray-600">
                      <FaMapMarkerAlt className="mr-2 text-[--primary-btn-color]" />
                      <span className="font-medium text-black">Place:</span>
                      <span className="ml-2">{festival.place}</span>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium text-black">Status:</span>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${festival.is_published === false
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                      }`}>
                      {festival.is_published === false ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Brief Description */}
            {festival.brief_description && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-[--secondary-color] mb-2">Brief Description</h3>
                <p className="text-gray-700 leading-relaxed">{festival.brief_description}</p>
              </div>
            )}

            {/* Full Description */}
            {festival.description && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-[--secondary-color] mb-2">Description</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{festival.description}</p>
              </div>
            )}

            {/* Footer */}
            <div className="flex justify-end mt-6">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

function Festival() {
  const [festivals, setFestivals] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    urdu_name: '',
    hindi_name: '',
    description: '',
    brief_description: '',
    year: '',
    place: '',
    no: 0,
    image_url: '',
    is_published: true
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedFestival, setSelectedFestival] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchFestivals();
  }, []);

  const fetchFestivals = async () => {
    setIsLoading(true);
    try {
      const response = await ApiService.GetFestival();
      console.log('API Response:', response);
      const data = response.data.responseContent;

      const normalizedFestivals = data.map(normalizeFestivalData);
      setFestivals(normalizedFestivals);
    } catch (error) {
      console.error('Error fetching festivals:', error);
      alert('Failed to fetch festivals');
      setFestivals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredFestivals = () => {
    if (!Array.isArray(festivals)) {
      console.error('festivals is not an array:', festivals);
      return [];
    }

    return festivals.filter(festival =>
      festival.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (festival.urdu_name && festival.urdu_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (festival.hindi_name && festival.hindi_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (festival.description && festival.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (festival.brief_description && festival.brief_description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const filteredFestivals = getFilteredFestivals();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }

      setImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.name) {
        alert('Please enter festival name');
        setIsSubmitting(false);
        return;
      }

      const payload = new FormData();
      payload.append('festival_name', formData.name);

      if (formData.urdu_name) {
        payload.append('festival_urdu', formData.urdu_name);
      }
      if (formData.hindi_name) {
        payload.append('festival_hindi', formData.hindi_name);
      }
      if (formData.brief_description && formData.brief_description.trim()) {
        payload.append('brief_description', formData.brief_description.trim());
      }
      if (formData.description && formData.description.trim()) {
        payload.append('description', formData.description.trim());
      }
      if (formData.year) {
        payload.append('year', formData.year);
      }
      if (formData.place) {
        payload.append('place', formData.place);
      }
      if (imageFile) {
        payload.append("file", imageFile);
      }

      console.log('FormData contents:');
      for (let pair of payload.entries()) {
        console.log(pair[0] + ': ' + (typeof pair[1] === 'string' ? pair[1].substring(0, 50) + '...' : pair[1]));
      }

      if (formData.id) {
        await ApiService.UpdateFestival(formData.id, payload);
      } else {
        await ApiService.AddFestival(payload);
      }

      await fetchFestivals();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving festival:', error);
      alert(error.response?.data?.message || error.message || 'An error occurred while saving the festival');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      urdu_name: '',
      hindi_name: '',
      description: '',
      brief_description: '',
      year: '',
      place: '',
      no: 0,
      is_published: true
    });
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEdit = (festival) => {
    setFormData({
      id: festival.id,
      name: festival.name,
      urdu_name: festival.urdu_name || '',
      hindi_name: festival.hindi_name || '',
      description: festival.description || '',
      brief_description: festival.brief_description || '',
      year: festival.year || '',
      place: festival.place || '',
      no: festival.no,
      is_published: festival.is_published
    });

    if (festival.image_url) {
      setImagePreview(festival.image_url);
    } else {
      setImagePreview(null);
    }
    setImageFile(null);

    setIsModalOpen(true);
  };

  const handleView = (festival) => {
    setSelectedFestival(festival);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedFestival(null);
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this festival?");
    if (!confirmDelete) return;

    try {
      await ApiService.DeleteFestival(id);
      await fetchFestivals();
    } catch (error) {
      console.error('Error deleting festival:', error);
      alert('Failed to delete festival');
    }
  };



  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === false ? true : false;
      await ApiService.ToggleFestival(id, newStatus);
      await fetchFestivals();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update festival status');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[--primary-color]"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto p-4" id='primary-fonts'>
      <h1 className="text-3xl font-bold mb-8 text-[--text-color] flex items-center gap-2">
        <FaCalendarAlt className="text-[--primary-color]" /> Festival List
      </h1>

      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div className="relative w-full md:w-96">
          <input
            type="text"
            placeholder="Search festivals..."
            className="w-full pl-10 pr-4 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FaSearch className="absolute left-3 top-3 text-gray-400" />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition duration-200 shadow-md"
          >
            <FaPlus /> Add Festival
          </button>
        </div>
      </div>

      {filteredFestivals.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
          {filteredFestivals.map((festival) => (
            <FestivalCard
              key={festival.id}
              festival={festival}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              onView={handleView}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-10">
          <p className="text-lg text-gray-600">
            {searchTerm ? `No festivals found matching "${searchTerm}"` : "No festivals available"}
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-[--primary-btn-color] hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {/* Detail View Modal */}
      <DetailViewModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
        festival={selectedFestival}
      />

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-screen-md max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold text-[--text-color] mb-4">
                  {formData.id ? 'Edit Festival' : 'Add New Festival'}
                </h2>
                <form onSubmit={handleSubmit}>
                  {/* Image Upload Section */}
                  <div className="mb-4 md:col-span-2">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Festival Image
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden border-2 border-[--border-color-o]">
                        <img
                          src={imagePreview || formData.image_url || getDefaultImage(formData.name || 'Festival')}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          id="image_upload"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                          disabled={isLoading}
                        />
                        <p className="text-xs text-gray-500 mt-1">Select an image file (JPG, PNG, WEBP - Max 5MB)</p>
                        {imageFile && (
                          <p className="text-xs text-green-600 mt-1">
                            Image selected: {imageFile.name}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Festival Name (English) *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                      required
                      placeholder="Enter festival name in English"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Festival Name (Urdu)
                    </label>
                    <input
                      type="text"
                      name="urdu_name"
                      value={formData.urdu_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                      dir="rtl"
                      id="primary-urdu-fonts"
                      placeholder="اردو میں تہوار کا نام"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Festival Name (Hindi)
                    </label>
                    <input
                      type="text"
                      name="hindi_name"
                      value={formData.hindi_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                      placeholder="हिंदी में त्योहार का नाम"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-[--text-color] font-bold mb-2">
                        Year
                      </label>
                      <input
                        type="text"
                        name="year"
                        value={formData.year}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                        placeholder="e.g., 2025"
                      />
                    </div>
                    <div>
                      <label className="block text-[--text-color] font-bold mb-2">
                        Place
                      </label>
                      <input
                        type="text"
                        name="place"
                        value={formData.place}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                        placeholder="e.g., India"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Brief Description
                    </label>
                    <textarea
                      name="brief_description"
                      value={formData.brief_description}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                      rows="3"
                      placeholder="Brief description about the festival"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-[--text-color] font-bold mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-[--border-color-o] rounded-lg focus:outline-none focus:ring-1 focus:ring-[--border-color-o]"
                      rows="3"
                      placeholder="Detailed description about the festival"
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                          Saving...
                        </>
                      ) : formData.id ? (
                        'Update Festival'
                      ) : (
                        'Add Festival'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

export default Festival;